import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IconUsersGroup } from '@tabler/icons-react';
import { CrudMode, CrudSchemaField, type EnsoFormItem } from '@ohpen/enso-ui';
import { Form } from 'antd';

import { TabProps } from '../interfaces';
import { FieldType } from '@pmp/api';
import { OPERATION_ORDER } from '../utils';

export const Packaging: React.FC<TabProps> = ({ form, mode, selectsOptions }) => {
  const { t } = useTranslation();
  const readonly = mode === CrudMode.VIEW;
  const watchedOperations = Form.useWatch(['registrationDetails', 'operations'], form);

  const operations: string[] = useMemo(() => {
    if (!watchedOperations) return [];
    return [...watchedOperations].sort((a, b) => OPERATION_ORDER.indexOf(a) - OPERATION_ORDER.indexOf(b));
  }, [watchedOperations]);

  const hasPackagingOperation = operations.includes('PACKAGE');

  const packagerOptions = useMemo(
    () => selectsOptions[FieldType.PACKAGERS] ?? [],
    [selectsOptions],
  );

  const columns: EnsoFormItem[] = useMemo(
    () => [
      {
        valueType: 'group',
        columns: hasPackagingOperation
          ? [
              {
                title: t('lender.firms.labels.thisFirmIsPackagerFor'),
                dataIndex: ['packaging', 'packagerForFirmIds'],
                valueType: 'select',
                readonly,
                readonlyDisplay: 'tag',
                fieldProps: {
                  optionFilterProp: 'label',
                  mode: 'multiple',
                  options: selectsOptions[FieldType.FIRMS] ?? [],
                },
                formItemProps: {
                  required: true,
                },
                colProps: { span: 12 },
              },
            ]
          : [
              {
                title: t('lender.firms.labels.packagersCanCreateCasesForThisFirm'),
                dataIndex: ['packaging', 'packagerFirmIds'],
                valueType: 'select',
                readonly,
                readonlyDisplay: 'tag',
                fieldProps: {
                  optionFilterProp: 'label',
                  mode: 'multiple',
                  options: packagerOptions,
                },
                colProps: { span: 12 },
              },
            ],
      },
    ],
    [readonly, t, hasPackagingOperation, selectsOptions, packagerOptions],
  );

  const schema: EnsoFormItem = useMemo(
    () => ({
      valueType: 'group',
      variant: 'card',
      icon: <IconUsersGroup />,
      title: t('lender.firms.labels.packaging'),
      columns,
    }),
    [columns, t],
  );

  return <CrudSchemaField item={schema} form={form} readonly={readonly} />;
};
